import { MaybeNil } from '../../utils/interfaces';
import { isNil } from 'lodash';
import { Fragment, ReactNodeArray, useMemo } from 'react';

export const Linkify = ({ text }: { text: MaybeNil<string> }) =>
  useMemo(() => {
    if (isNil(text)) {
      return null;
    }

    // Array to store the split parts
    const result: ReactNodeArray = [];

    // Position to start searching for the next match
    let lastIndex = 0;

    // replace text to <a /> if link exists
    // https://stackoverflow.com/questions/49634850/convert-plain-text-links-to-clickable-links
    // URLs starting with http://, https://, or ftp://
    //
    // ** Beware ** JavaScript RegExp objects are stateful when they have the global
    // or sticky flags set (e.g., /foo/g or /foo/y). They store a lastIndex
    // from the previous match. Using this internally, test() can be used to
    // iterate over multiple matches in a string of text (with capture groups).
    // Ref: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/test
    const linkPattern =
      /(\b(https?|ftp):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gim;

    // Iterate over matches using exec() method
    for (let match, i = 0; (match = linkPattern.exec(text)) !== null; i++) {
      // Push the part before the match (if any)
      if (match.index !== lastIndex) {
        result.push(
          <Fragment key={`linkify-before-${i}`}>
            {text.substring(lastIndex, match.index)}
          </Fragment>,
        );
      }

      // Push the matched part
      const link = match[0];
      result.push(
        <a
          href={link}
          target="_blank"
          key={`linkify-link-${i}`}
          rel="noreferrer"
        >
          {link}
        </a>,
      );
      // Update the position to start searching for the next match
      lastIndex = linkPattern.lastIndex;
    }

    // Push the remaining part of the string (after the last match)
    if (lastIndex < text.length) {
      result.push(
        <Fragment key="linkify-last">{text.substring(lastIndex)}</Fragment>,
      );
    }

    return <>{result}</>;
  }, [text]);
